import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { getSiteSettings } from '@/lib/siteSettings';
import { toast } from 'sonner';
import { Instagram, Facebook, Twitter, Linkedin, Youtube, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [settings, setSettings] = useState(null);
  const [footerContent, setFooterContent] = useState(null);

  useEffect(() => {
    getSiteSettings().then(r => setSettings(r.data)).catch(console.error);
    axios.get(`${API}/page-content/footer`).then(r => setFooterContent(r.data)).catch(() => {});
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubscribing(true);
    const fd = new FormData();
    fd.append('email', email);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, fd);
      toast.success(res.data.message);
      setEmail('');
    } catch { toast.error('Failed to subscribe.'); }
    finally { setIsSubscribing(false); }
  };

  const socialIcons = { instagram: Instagram, facebook: Facebook, twitter: Twitter, linkedin: Linkedin, youtube: Youtube };

  return (
    <footer className="bg-card border-t border-border mt-16 sm:mt-24">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24 py-10 sm:py-16">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-12">
          {/* About */}
          <div>
            <h3 className="font-serif text-xl sm:text-2xl font-light mb-3">{settings?.site_name || 'Gangadhar Som'}</h3>
            <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed">
              {footerContent?.about_text || "Renowned professional artist with 40+ years in fine arts and South Indian cinema. Creator of the 46 Lord Hanuman life history paintings at Karya Siddhi Hanuman Temple, Dallas. Honored with the title 'Datta Peetha Asthan Chitrakara'."}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-sans uppercase tracking-[0.2em] text-[10px] sm:text-xs font-semibold mb-3">Quick Links</h3>
            <ul className="space-y-1.5 sm:space-y-2">
              {['/', '/about', '/gallery', '/exhibitions', '/press', '/contact'].map(p => (
                <li key={p}>
                  <Link to={p} className="font-sans text-xs sm:text-sm text-muted-foreground hover:text-accent transition-colors link-underline inline-block">
                    {p === '/' ? 'Home' : p.slice(1).charAt(0).toUpperCase() + p.slice(2)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div className="sm:col-span-2 md:col-span-1">
            <h3 className="font-sans uppercase tracking-[0.2em] text-[10px] sm:text-xs font-semibold mb-3">{footerContent?.newsletter_title || 'Newsletter'}</h3>
            <p className="font-sans text-xs sm:text-sm text-muted-foreground mb-3">{footerContent?.newsletter_text || 'Subscribe for updates on new artworks and exhibitions.'}</p>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" required
                className="flex-1 min-w-0 px-3 py-2 bg-background border border-border rounded-lg font-sans text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-accent" />
              <button type="submit" disabled={isSubscribing}
                className="px-3 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-50 flex-shrink-0">
                <Mail size={16} />
              </button>
            </form>

            {settings?.social_links && Object.values(settings.social_links).some(l => l) && (
              <div className="mt-5">
                <h3 className="font-sans uppercase tracking-[0.2em] text-[10px] sm:text-xs font-semibold mb-3">Follow Us</h3>
                <div className="flex gap-2">
                  {Object.entries(settings.social_links).map(([platform, url]) => {
                    if (!url) return null;
                    const Icon = socialIcons[platform];
                    return (
                      <motion.a key={platform} href={url} target="_blank" rel="noopener noreferrer"
                        whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}
                        className="w-9 h-9 bg-background border border-border rounded-full flex items-center justify-center hover:bg-accent hover:text-white hover:border-accent transition-all">
                        <Icon size={16} />
                      </motion.a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 sm:mt-12 pt-6 border-t border-border flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="font-sans text-xs text-muted-foreground text-center sm:text-left">
            {settings?.footer_text || `\u00A9 ${new Date().getFullYear()} Gangadhar Som. All rights reserved.`}
          </p>
          <Link to="/privacy" className="font-sans text-[10px] sm:text-xs text-muted-foreground hover:text-accent transition-colors mr-4">Privacy</Link>
          <Link to="/admin" className="font-sans text-[10px] sm:text-xs text-muted-foreground hover:text-accent transition-colors">Admin</Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
